import { useActionConfirmation } from '../../components/ActionFeedbackProvider';
// React and hooks
import React, { useEffect, useState, useRef } from 'react';
// Icons
import { Home, Trash, LogIn, LogOut, Search, Sun, Moon, ChevronLeft, ChevronRight } from 'lucide-react';
// Router
import { useNavigate } from 'react-router-dom';
// Animation
import { motion } from 'framer-motion';
// Hooks
import useTheme from '../../hooks/useTheme';
import InventoryStockLed from '../../components/InventoryStockLed';
// API
import api from '../../lib/api';
// i18n
import { useTranslation } from 'react-i18next';

const PISColdStorage1 = () => {
  // States
  const [entries, setEntries] = useState([]);
  const [exits, setExits] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Cache refs (performance)
  const brandsCache = useRef(null);
  const categoriesCache = useRef(null);

  // Busy states for delete operations
  const [busyDeleteEntries, setBusyDeleteEntries] = useState(false);
  const [busyDeleteExits, setBusyDeleteExits] = useState(false);
  const [busyDeleteInventory, setBusyDeleteInventory] = useState(false);

  // Selected date state (default = today)
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date().toISOString().split("T")[0];
    return today;
  });

  // Hooks
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const confirmAction = useActionConfirmation();

  // Fixed key for this screen
  const locationKey = "coldstorage1";

  // Main button style (navigation buttons)
  const baseBtn =
    "flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow transition duration-200 " +
    "bg-black text-white hover:bg-white hover:text-black hover:scale-105 " +
    "dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white";

  // Delete button style
  const deleteBtn =
    "p-2 rounded-md text-white " +
    "bg-red-600 hover:bg-red-700 " +
    "dark:bg-red-500 dark:hover:bg-red-600";

  // Helpers
  const getId = (obj) => obj?.id || obj?._id;
  const eqId = (a, b) => String(a || '') === String(b || '');

  // Load all data - Focused on speed and caching
  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        setError('');

        const promises = [
          api.get(`/inventory/entries?location=${locationKey}&date=${selectedDate}`),
          api.get(`/inventory/exits?location=${locationKey}&date=${selectedDate}`),
          api.get(`/inventory/summary?location=${locationKey}`),
        ];

        if (!brandsCache.current) promises.push(api.get('/brands'));
        if (!categoriesCache.current) promises.push(api.get('/categories'));

        const results = await Promise.all(promises);

        const enRes = results[0];
        const exRes = results[1];
        const invRes = results[2];
        
        if (!brandsCache.current && results[3]) brandsCache.current = results[3].data;
        if (!categoriesCache.current && results[4]) categoriesCache.current = results[4].data;

        setBrands(Array.isArray(brandsCache.current) ? brandsCache.current : []);
        setCategories(Array.isArray(categoriesCache.current) ? categoriesCache.current : []);
        setEntries(Array.isArray(enRes.data) ? enRes.data.slice().reverse() : []);
        setExits(Array.isArray(exRes.data) ? exRes.data.slice().reverse() : []);

        const locationSummary = invRes.data?.[locationKey];
        setSummaries(Array.isArray(locationSummary?.products) ? locationSummary.products : []);

      } catch (err) {
        setError(err?.response?.data?.message || t('errorLoadData'));
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [t, selectedDate]);

  /**
   * REFRESH SUMMARY
   * Re-syncs only the inventory column to ensure visibility of products with zero stock
   */
  const refreshSummary = async () => {
    try {
      const res = await api.get(`/inventory/summary?location=${locationKey}`);
      const locationSummary = res.data?.[locationKey];
      setSummaries(Array.isArray(locationSummary?.products) ? locationSummary.products : []);
    } catch (err) {
      console.error("Error refreshing summary:", err);
    }
  };

  /**
   * LOCAL INVENTORY UPDATE (Instant Math)
   */
  const updateLocalInventory = (productId, diffBoxes, diffWeight) => {
    setSummaries(prev => prev.map(item => {
      if (item.productId === productId) {
        return {
          ...item,
          totalBoxes: (item.totalBoxes || 0) + diffBoxes,
          totalWeight: (item.totalWeight || 0) + diffWeight
        };
      }
      return item;
    }));
  };

  const handleDeleteEntry = async (id) => {
    if (!await confirmAction(t('confirmDeleteEntry'))) return;
    const entryToDelete = entries.find(e => getId(e) === id);
    if (!entryToDelete) return;

    setEntries(prev => prev.filter(item => getId(item) !== id));
    updateLocalInventory(entryToDelete.productId, -entryToDelete.quantityEntered, -(entryToDelete.totalWeight || 0));
    
    try {
      await api.delete(`/inventory/entries/${id}`);
    } catch (err) {
      console.error(err);
      refreshSummary();
    }
  };

  const handleDeleteExit = async (id) => {
    if (!await confirmAction(t('confirmDeleteExit'))) return;
    const exitToDelete = exits.find(x => getId(x) === id);
    if (!exitToDelete) return;

    setExits(prev => prev.filter(item => getId(item) !== id));
    
    try {
      await api.delete(`/inventory/exits/${id}`);
      // Sincronización para que productos que vuelvan a stock > 0 reaparezcan
      await refreshSummary();
    } catch (err) {
      console.error(err);
      refreshSummary();
    }
  };

  const handleDeleteAllEntries = async () => {
    if (!await confirmAction(t('confirmDeleteAllEntries'))) return;
    setBusyDeleteEntries(true);
    const ids = entries.map((e) => getId(e)).filter(Boolean);
    setEntries([]);
    try {
      await Promise.allSettled(ids.map((id) => api.delete(`/inventory/entries/${id}`)));
      await refreshSummary();
    } finally {
      setBusyDeleteEntries(false);
    }
  };

  const handleDeleteAllExits = async () => {
    if (!await confirmAction(t('confirmDeleteAllExits'))) return;
    setBusyDeleteExits(true);
    const ids = exits.map((x) => getId(x)).filter(Boolean);
    setExits([]);
    try {
      await Promise.allSettled(ids.map((id) => api.delete(`/inventory/exits/${id}`)));
      await refreshSummary();
    } finally {
      setBusyDeleteExits(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!await confirmAction(t('confirmDeleteProduct'))) return;
    setBusyDeleteInventory(true);
    setSummaries(prev => prev.filter(s => s.productId !== productId));
    try {
      await api.delete(`/products/${productId}`);
    } catch {
      alert(t('errorDeleteProduct'));
      refreshSummary();
    } finally {
      setBusyDeleteInventory(false);
    }
  };

  const term = search.toLowerCase();

  const filteredEntries = entries.filter(
    (e) =>
      e.productName?.toLowerCase().includes(term) ||
      (brands.find((x) => eqId(getId(x), e.brandId))?.name || '').toLowerCase().includes(term) ||
      (categories.find((c) => eqId(getId(c), e.categoryId))?.name || '').toLowerCase().includes(term) ||
      (e.supplierName || '').toLowerCase().includes(term)
  );

  const filteredExits = exits.filter(
    (x) =>
      x.productName?.toLowerCase().includes(term) ||
      (brands.find((b) => eqId(getId(b), x.brandId))?.name || '').toLowerCase().includes(term) ||
      (categories.find((c) => eqId(getId(c), x.categoryId))?.name || '').toLowerCase().includes(term) ||
      (x.supplierName || '').toLowerCase().includes(term)
  );

  // Filtro ajustado: No ocultamos productos con stock 0
  const filteredSummaries = summaries.filter(
    (s) =>
      (s.name?.toLowerCase().includes(term) ||
        (brands.find((b) => eqId(getId(b), s.brandId))?.name || '').toLowerCase().includes(term) ||
        (categories.find((c) => eqId(getId(c), s.categoryId))?.name || '').toLowerCase().includes(term) ||
        (s.supplierName || '').toLowerCase().includes(term))
  );

  const shiftDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split("T")[0]);
  };

  const getDayName = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    const dayIndex = date.getDay();
    return t(`days.${dayIndex}`);
  };

  return (
    <motion.div
      className="px-1 py-4 sm:px-3 sm:py-6 lg:px-6 lg:py-8 relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <button onClick={toggleTheme} className="absolute top-4 right-4 text-black dark:text-white">
        {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
      </button>

      <div className="flex flex-wrap justify-center gap-3 mb-6">
        <button onClick={() => navigate('/coldstorage1/entry')} className={baseBtn}>
          <LogIn size={16} /> {t('entry')}
        </button>
        <button onClick={() => navigate('/')} className={baseBtn}>
          <Home size={16} /> {t('home')}
        </button>
        <button onClick={() => navigate('/coldstorage1/exit')} className={baseBtn}>
          <LogOut size={16} /> {t('exit')}
        </button>
      </div>

      <div className="flex items-center justify-center gap-4 mb-4 w-full">
        <button className={`${baseBtn} !px-2 !py-2 rounded-full`} onClick={() => shiftDate(-1)}>
          <ChevronLeft size={20} />
        </button>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm shadow w-48 text-center
                     bg-white text-black dark:bg-gray-900 dark:text-white dark:border-gray-600
                     [color-scheme:light] dark:[color-scheme:dark]"
        />
        <button className={`${baseBtn} !px-2 !py-2 rounded-full`} onClick={() => shiftDate(1)}>
          <ChevronRight size={20} />
        </button>
      </div>

      <h1 className="text-3xl font-bold text-center mb-6">{getDayName(selectedDate)}</h1>
      <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 text-center">
        {t('coldstorage1Inventory')}
      </h2>

      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border rounded-lg px-3 py-2 shadow-md">
          <Search size={18} className="text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('searchPlaceholder')}
            className="w-full bg-transparent outline-none text-gray-700 dark:text-gray-200"
          />
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto mb-4 rounded-lg bg-red-600/90 text-white px-4 py-2">
          {error}
        </div>
      )}

      <div className={`max-w-7xl mx-auto inventory-columns grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 text-sm transition-opacity duration-200 ease-in-out ${loading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>

        {/* --- ENTRIES --- */}
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl p-3 sm:p-4 lg:p-6 backdrop-blur-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">{t('entryHistory')}</h2>
            <button className={deleteBtn} onClick={handleDeleteAllEntries} disabled={busyDeleteEntries || entries.length === 0}>
              <Trash size={16} />
            </button>
          </div>
          <div className="space-y-2">
            {filteredEntries.map((e) => (
              <div key={getId(e)} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md border flex justify-between items-start">
                <div>
                  <p className="font-semibold">{e.productName}</p>
                  <p>{t('brand')}: {(brands.find((x) => eqId(getId(x), e.brandId))?.name) || '-'}</p>
                  <p>{t('category')}: {categories.find((c) => eqId(getId(c), e.categoryId))?.name || '-'}</p>
                  <p>{t('supplier')}: {e.supplierName || '-'}</p>
                  <p>{t('boxes')}: {e.quantityEntered}</p>
                  <p>{t('weight')}: {(e.totalWeight ?? 0).toFixed(2)} KG</p>
                  {e.notes && <p className="italic text-gray-600 dark:text-gray-300">{t('notes')}: {e.notes}</p>}
                  {e.enteredAt && <p className="text-xs text-gray-500">{new Date(e.enteredAt).toLocaleString()}</p>}
                </div>
                <button onClick={() => handleDeleteEntry(getId(e))} className={deleteBtn}>
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* --- INVENTORY --- */}
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl p-3 sm:p-4 lg:p-6 backdrop-blur-md">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">{t('currentInventory')}</h2>
          <div className="space-y-2">
            {filteredSummaries.map((s) => (
              <div key={s.productId} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md border flex justify-between items-start">
                <div>
                  <p className="font-semibold">{s.name}</p>
                  <p>{t('brand')}: {(brands.find((x) => eqId(getId(x), s.brandId))?.name) || '-'}</p>
                  <p>{t('category')}: {s.categoryName || '-'}</p>
                  <p>{t('supplier')}: {s.supplierName || '-'}</p>
                  <p className={`${(s.totalBoxes ?? 0) <= 0 ? 'text-red-500 font-bold' : ''}`}>
                    {t('boxes')}: {s.totalBoxes ?? 0}
                    <InventoryStockLed location={locationKey} quantity={s.totalBoxes} />
                  </p>
                  <p>{t('weight')}: {(s.totalWeight ?? 0).toFixed(2)} KG</p>
                </div>
                <button onClick={() => handleDeleteProduct(s.productId)} className={deleteBtn} disabled={busyDeleteInventory}>
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* --- EXITS --- */}
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl p-3 sm:p-4 lg:p-6 backdrop-blur-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">{t('exitHistory')}</h2>
            <button className={deleteBtn} onClick={handleDeleteAllExits} disabled={busyDeleteExits || exits.length === 0}>
              <Trash size={16} />
            </button>
          </div>
          <div className="space-y-2">
            {filteredExits.map((x) => (
              <div key={getId(x)} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md border flex justify-between items-start">
                <div>
                  <p className="font-semibold">{x.productName}</p>
                  <p>{t('brand')}: {(brands.find((b) => eqId(getId(b), x.brandId))?.name) || '-'}</p>
                  <p>{t('category')}: {categories.find((c) => eqId(getId(c), x.categoryId))?.name || '-'}</p>
                  <p>{t('supplier')}: {x.supplierName || '-'}</p>
                  <p>{t('boxes')}: {x.quantityExited}</p>
                  <p>{t('weight')}: {(x.totalWeight ?? 0).toFixed(2)} KG</p>
                  <p className="italic text-gray-600 dark:text-gray-300">{t('exitClient')}: {x.clientName || t('noClient')}</p>
                  {x.notes && <p className="italic text-gray-600 dark:text-gray-300">{t('notes')}: {x.notes}</p>}
                  {x.exitedAt && <p className="text-xs text-gray-500">{new Date(x.exitedAt).toLocaleString()}</p>}
                </div>
                <button onClick={() => handleDeleteExit(getId(x))} className={deleteBtn}>
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PISColdStorage1;
